import { useMemo } from 'preact/hooks';
import styles from './ProductListLayout.module.scss';
import {
  groupProductsByCollection,
  productTypeToSuperCategoryMap,
  type Product
} from '@/domain/Product';
import ErrorBoundary from '@/widgets/components/containers/ErrorBoundary/ErrorBoundary.tsx';
import FilterBar from '../filters/FilterBar/FilterBar.tsx';
import FilterSelections from '../filters/FilterSelections/FilterSelections.tsx';
import { initFiltersAndSort } from '@/widgets/services/FilteringAndSortingService.ts';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import type { PersistedState } from '../Store.ts';
import {
  createFilters,
  createSortOptions
} from '@/domain/FilteringAndSorting.ts';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '../config/index.ts';
import useProductListTitle from '../hooks/useProductListTitle.ts';
import { useFilteringAndSortingPersistence } from '../hooks/useFilteringAndSortingPersistence.tsx';
import ProductCardsGrid from '../cards/ProductCardsGrid/ProductCardsGrid.tsx';
import type { Categories, Category } from '@/domain/Category.ts';
import { CardModalIdProvider } from '../cards/hooks/useCardModalId.tsx';
import type { I18NInstance } from '@/utils/createI18nInstance.ts';
import type { ProductTranslations } from '@/domain/config/index.ts';

import { promotionCardIsInCollection } from './utils.ts';
import useScrollToAnchor from '@/widgets/hooks/useScrollToAnchor.tsx';
import { cleanProductsOfCollection } from '../utils/cleanCollections.ts';
import { CollectionDetails } from '../CollectionDetails/CollectionDetails.tsx';
import NotificationContainer from '@/widgets/components/controls/Notifications/NotificationContainer/NotificationContainer.tsx';

type ProductListLayoutProps = {
  products: Product[];
  categories: Categories;
  productI18nInstance: I18NInstance<ProductTranslations>;
};

const DEFAULT_STICKY_HEADER_HEIGHT = 180;
export default function ProductListLayout({
  products,
  categories,
  productI18nInstance
}: ProductListLayoutProps) {
  const [proxyQueryState] = useQueryStringStore<PersistedState>();
  const storedSort = proxyQueryState.sort.value;
  const storedFilters = proxyQueryState.filters.value;
  const { productType, technology } = useRoute();
  const {
    filters = [],
    sortOptions = [],
    promotionCards = []
  } = useWidgetOptions<Options>();

  const filtersAndSort = useMemo(
    () =>
      initFiltersAndSort(
        products,
        createFilters(filters, products, productI18nInstance, categories),
        createSortOptions(sortOptions),
        productI18nInstance,
        storedFilters,
        storedSort
      ),
    [products]
  );

  useScrollToAnchor(DEFAULT_STICKY_HEADER_HEIGHT);
  useFilteringAndSortingPersistence(filtersAndSort);

  const { filtering, sorting } = filtersAndSort;

  const isFilteredOrSorted =
    filtering.isFiltered.value || sorting.isSorted.value;

  const collections: Category[] = useMemo(() => {
    const superCategory = productTypeToSuperCategoryMap[productType];
    return categories.get(superCategory)?.subCategories ?? [];
  }, [categories, productType]);

  const groupedProductsByCollection: { [key: string]: Product[] } = useMemo(
    () => groupProductsByCollection(products, collections),
    [products]
  );

  const filtersAndSortTitle = useProductListTitle({ filtersAndSort });
  const { displayCollectionSubheader } = useWidgetOptions<Options>();

  return (
    <div class={styles.container}>
      {/* uncomment if you want to see a demo of notifications  */}
      {/* <NotificationQuickDemo /> */}

      <ErrorBoundary>
        <FilterBar filtersAndSort={filtersAndSort} productType={productType} />
      </ErrorBoundary>

      {filtering.isFiltered.value ? (
        <ErrorBoundary>
          <FilterSelections filtersAndSort={filtersAndSort} />
        </ErrorBoundary>
      ) : null}

      <ErrorBoundary>
        {isFilteredOrSorted && filtersAndSort.products.value.length > 0 ? (
          <CardModalIdProvider collectionCode="-">
            <h2 class={styles.heading}>{filtersAndSortTitle}</h2>
            <ProductCardsGrid
              products={filtersAndSort.products.value}
              title={filtersAndSortTitle}
            />
          </CardModalIdProvider>
        ) : (
          <>
            {collections.map((collection) => {
              let productsForCollection: Product[] | null =
                groupedProductsByCollection[collection.code];

              productsForCollection =
                productsForCollection !== undefined
                  ? cleanProductsOfCollection(
                      productsForCollection,
                      technology,
                      productType
                    )
                  : null;

              if (productsForCollection === null) return null;

              const promotionCardsByCollection = promotionCards.filter((card) =>
                promotionCardIsInCollection(card, collection)
              );

              return (
                <CollectionDetails
                  collection={collection}
                  displayCollectionSubheader={displayCollectionSubheader}
                  productsForCollection={productsForCollection}
                  promotionCardsByCollection={promotionCardsByCollection}
                />
              );
            })}
          </>
        )}
      </ErrorBoundary>

      <NotificationContainer />
    </div>
  );
}

ProductListLayout.Skeleton = function Skeleton() {
  return (
    <div class={styles.container} data-nosnippet>
      <ErrorBoundary>
        <FilterBar.Skeleton />
        <ProductCardsGrid.Skeleton />
      </ErrorBoundary>
    </div>
  );
};
